/** Outbound tool detection via headers and known MTA domains. */

import type { FingerprintDetails, ReplyToDetails, BdrPhraseDetails } from '../types';
import { hasListUnsubscribeHeader } from './check-framework';
import { isDomainTrustedSync } from './trusted-domains';

// Known automation tools that appear in X-Mailer header values
const X_MAILER_AUTOMATION_TOOLS = [
  'outreach.io',
  'outreach',
  'lemlist',
  'woodpecker',
  'reply.io',
  'reply',
  'instantly.ai',
  'instantly',
  'apollo',
  'salesloft',
  'hubspot',
  'yesware',
  'mixmax',
  'mailshake',
  'snov.io',
  'snov',
  'hunter.io',
  'hunter',
  'lusha',
  'zoominfo',
  'seamless.ai',
  'seamless',
  'coldemail',
  'cold email',
  'coldoutreach',
  'cold outreach',
];

// Known outbound tool headers
const OUTBOUND_HEADERS = [
  'X-Mailer',
  'X-Sender',
  'X-Campaign',
  'X-Campaign-Id',
  'X-Salesloft',
  'X-Apollo',
  'X-HubSpot',
  'X-Outreach',
  'X-Yesware',
  'X-Mixmax',
];

// Known outbound tool MTAs
const OUTBOUND_MTAS = [
  'sendgrid.net',
  'mailgun.org',
  'amazonses.com',
  'mandrillapp.com',
  'sparkpostmail.com',
  'postmarkapp.com',
  'mailchimp.com',
  'constantcontact.com',
  'salesforce.com',
];

// Known outbound tool domains in From/Reply-To
const OUTBOUND_DOMAINS = [
  'salesloft.com',
  'apollo.io',
  'hubspot.com',
  'outreach.io',
  'yesware.com',
  'mixmax.com',
  'reply.io',
  'woodpecker.co',
  'lemlist.com',
  'instantly.ai',
];

export function checkOutboundFingerprints(
  headers: Record<string, string>,
  fromEmail: string,
  receivedHeaders: string[]
): [number, string[]] {
  /**
   * Check for outbound tool fingerprints in headers and domains.
   *
   * @param headers - Email headers.
   * @param fromEmail - From email address.
   * @param receivedHeaders - Received headers.
   * @returns Tuple of (score_penalty, reasons).
   */
  const details = checkOutboundFingerprintsWithDetails(headers, fromEmail, receivedHeaders);
  let score = 0;
  const reasons: string[] = [];

  if (details.x_mailer.present) {
    score += 1;
    if (details.x_mailer.matched_tool) {
      reasons.push(`Automation tool detected in X-Mailer: ${details.x_mailer.matched_tool}`);
    } else {
      reasons.push(`Outbound tool header detected: X-Mailer`);
    }
  }

  score += details.outbound_headers.length;
  details.outbound_headers.forEach((header) => {
    reasons.push(`Outbound tool header detected: ${header}`);
  });

  if (
    details.outbound_mtas.detected.length > 0 &&
    details.outbound_mtas.combined_with_other_factors
  ) {
    score += 0.5;
    reasons.push(
      `Outbound MTA detected: ${details.outbound_mtas.detected[0]} (combined with other factors)`
    );
  }

  score += details.outbound_domains.detected.length;
  details.outbound_domains.detected.forEach((domain) => {
    reasons.push(`Outbound tool domain detected: ${domain}`);
  });

  return [score, reasons];
}

export function checkOutboundFingerprintsWithDetails(
  headers: Record<string, string>,
  fromEmail: string,
  receivedHeaders: string[]
): FingerprintDetails {
  /**
   * Check for outbound tool fingerprints in headers and domains with detailed results.
   *
   * @param headers - Email headers.
   * @param fromEmail - From email address.
   * @param receivedHeaders - Received headers.
   * @returns Detailed fingerprint information.
   */
  const result: FingerprintDetails = {
    x_mailer: {
      present: false,
      value: null,
      matched_tool: null,
      is_generic: false,
    },
    outbound_headers: [],
    outbound_mtas: {
      detected: [],
      combined_with_other_factors: false,
    },
    outbound_domains: {
      detected: [],
      matched_domains: [],
    },
  };

  // Check X-Mailer header value for automation tools (more specific detection)
  const xMailerHeader = headers['X-Mailer'] || headers['x-mailer'] || '';
  if (xMailerHeader) {
    result.x_mailer.present = true;
    result.x_mailer.value = xMailerHeader;
    const xMailerLower = xMailerHeader.toLowerCase();
    let foundAutomationTool = false;
    for (const tool of X_MAILER_AUTOMATION_TOOLS) {
      if (xMailerLower.includes(tool.toLowerCase())) {
        result.x_mailer.matched_tool = tool;
        foundAutomationTool = true;
        break;
      }
    }
    // If X-Mailer exists but doesn't match known tools, still flag it (generic automation indicator)
    if (!foundAutomationTool) {
      result.x_mailer.is_generic = true;
    }
  }

  // Check other outbound tool headers (excluding X-Mailer since we already checked it)
  const headerKeys = Object.keys(headers).map((h) => h.toLowerCase());
  for (const headerName of OUTBOUND_HEADERS) {
    const headerNameLower = headerName.toLowerCase();
    // Skip X-Mailer since we already checked it above
    if (headerNameLower === 'x-mailer') {
      continue;
    }
    if (headerKeys.includes(headerNameLower)) {
      result.outbound_headers.push(headerName);
    }
  }

  // Check MTA domains in Received headers
  // Only flag if combined with other suspicious indicators (many legitimate services use these)
  for (const received of receivedHeaders) {
    for (const mta of OUTBOUND_MTAS) {
      if (received.toLowerCase().includes(mta.toLowerCase())) {
        result.outbound_mtas.detected.push(mta);
        break;
      }
    }
  }

  // Only flag if we have other suspicious indicators (outbound headers or domains)
  const hasOtherIndicators = result.x_mailer.present || result.outbound_headers.length > 0;
  result.outbound_mtas.combined_with_other_factors = hasOtherIndicators;

  // Check From/Reply-To domains
  // Use exact match or endsWith to avoid false positives (e.g., "hubspot.com" should not match "hubspot.com.example.com")
  const fromDomain = extractDomain(fromEmail);
  if (fromDomain) {
    const fromDomainLower = fromDomain.toLowerCase();
    for (const domain of OUTBOUND_DOMAINS) {
      const domainLower = domain.toLowerCase();
      // Exact match or subdomain match (e.g., "mail.hubspot.com" matches "hubspot.com")
      if (fromDomainLower === domainLower || fromDomainLower.endsWith(`.${domainLower}`)) {
        result.outbound_domains.detected.push(domain);
        result.outbound_domains.matched_domains.push(domain);
        break;
      }
    }
  }

  return result;
}

export function checkReplyToMismatch(
  headers: Record<string, string>,
  fromEmail: string,
  userTrustedDomains: string[] = []
): [number, string[]] {
  /**
   * Check if Reply-To header differs from From header.
   * Compares domains, not full email addresses, since same-domain replies are legitimate.
   * This is a common spam pattern where spammers use different reply domains.
   *
   * However, legitimate operational emails from trusted services may use different
   * domains for Reply-To (e.g., Sentry uses md.getsentry.com for From and getsentry.com
   * for Reply-To). If the Reply-To domain is from a trusted service, skip the penalty.
   *
   * @param headers - Email headers.
   * @param fromEmail - From email address.
   * @param userTrustedDomains - User's list of trusted domains (optional).
   * @returns Tuple of (score_penalty, reasons).
   */
  const details = checkReplyToMismatchWithDetails(headers, fromEmail);
  if (!details.matches_from && details.address) {
    const fromDomain = extractDomain(fromEmail);
    const replyToDomain = extractDomain(details.address);

    // If Reply-To domain is from a trusted service, don't flag the mismatch
    // This handles legitimate operational emails from trusted services
    if (replyToDomain && isDomainTrustedSync(replyToDomain, userTrustedDomains)) {
      return [0, []];
    }

    // Show domain comparison in reason message
    const domainInfo =
      fromDomain && replyToDomain ? ` (domains: ${fromDomain} vs ${replyToDomain})` : '';
    return [1, [`Reply-To domain differs from From domain${domainInfo}`]];
  }
  return [0, []];
}

export function checkReplyToMismatchWithDetails(
  headers: Record<string, string>,
  fromEmail: string
): ReplyToDetails {
  /**
   * Check if Reply-To header differs from From header with detailed results.
   * Compares domains, not full email addresses, since same-domain replies are legitimate.
   *
   * @param headers - Email headers.
   * @param fromEmail - From email address.
   * @returns Detailed Reply-To information.
   */
  const replyTo = headers['Reply-To'] || headers['reply-to'] || '';
  if (!replyTo) {
    // No Reply-To header is fine (not suspicious)
    return {
      address: null,
      matches_from: true,
    };
  }

  // Extract domains from From and Reply-To (compare domains, not full addresses)
  const fromDomain = extractDomain(fromEmail);
  const replyToDomain = extractDomain(replyTo);

  if (fromDomain && replyToDomain) {
    const fromDomainLower = fromDomain.toLowerCase();
    const replyToDomainLower = replyToDomain.toLowerCase();

    // Extract full address for display purposes
    const replyToMatch = replyTo.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    const replyToAddr = replyToMatch ? replyToMatch[0].toLowerCase() : null;

    return {
      address: replyToAddr,
      matches_from: domainsMatchOrSubdomain(fromDomainLower, replyToDomainLower),
    };
  }

  // If we can't extract domains, extract address for display but assume it matches (conservative)
  const replyToMatch = replyTo.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return {
    address: replyToMatch ? replyToMatch[0].toLowerCase() : null,
    matches_from: true, // If we can't extract, assume it matches (conservative)
  };
}

export function extractDomain(email: string): string | null {
  /**
   * Extract domain from email address.
   * Handles both "email@domain.com" and "Name <email@domain.com>" formats.
   *
   * @param email - Email address.
   * @returns Domain, or null if invalid.
   */
  // Extract email from "Name <email>" format if present
  const emailOnly = email.includes('<') ? email.match(/<([^>]+)>/)?.[1] || email : email;

  // Extract domain part after @
  const match = emailOnly.match(/@([^\s<>]+)/);
  if (match) {
    return match[1];
  }
  return null;
}

function domainsMatchOrSubdomain(domainA?: string | null, domainB?: string | null): boolean {
  if (!domainA || !domainB) {
    return false;
  }
  if (domainA === domainB) {
    return true;
  }
  return domainA.endsWith(`.${domainB}`) || domainB.endsWith(`.${domainA}`);
}

export function checkBdrPhrases(text: string): [number, string[]] {
  /**
   * Check for classic BDR phrases in text.
   * More conservative: requires multiple phrases or very specific spam phrases.
   *
   * @param text - Text to check.
   * @returns Tuple of (score_penalty, reasons).
   */
  const details = checkBdrPhrasesWithDetails(text);
  let score = 0;
  const reasons: string[] = [];

  // Score high-confidence phrases
  score += details.high_confidence.count;
  details.high_confidence.phrases_found.forEach((phrase) => {
    reasons.push(`High-confidence BDR phrase: '${phrase}'`);
  });

  // Only score low-confidence phrases if we have 2+ of them
  if (details.low_confidence.scored) {
    score += Math.min(details.low_confidence.count * 0.5, 1.5); // Cap at 1.5
    reasons.push(`Multiple BDR phrases detected (${details.low_confidence.count} phrases)`);
  }

  return [score, reasons];
}

/**
 * Detects "manual unsubscribe" language (e.g., "reply 'unsubscribe'") and returns +2 points
 * when the email does NOT include a standards-based List-Unsubscribe header.
 *
 * Rationale: asking recipients to reply to unsubscribe is a common cold-outreach/spam pattern.
 */
export function checkManualUnsubscribeRequest(
  headers: Record<string, string>,
  text: string
): [number, string[]] {
  if (!text) return [0, []];

  // If the sender provides standards-based unsubscribe headers, don't apply this penalty.
  if (hasListUnsubscribeHeader(headers)) return [0, []];

  // Match common manual opt-out prompts (quotes optional, punctuation allowed).
  // Covers verb variants: “reply”, “respond”, “send back”, “send” (with/to optional).
  // “unsubscribe” alone is common in legit newsletters, so we require a manual-action verb.
  const replyOptOutRegex =
    /\b(?:reply|respond|send\s+back|send)\b(?:\s+with|\s+to)?\s+["'“”]?\s*(unsubscribe|no\s+thanks|no\s+thank\s+you|not\s+interested|opt\s*out|stop|remove\s+me|remove)\s*["'“”]?\s*[.!?,;:]?/i;
  const replyStopToUnsubscribeRegex =
    /\b(?:reply|respond|send\s+back|send)\b\s+stop\b[\s\S]{0,60}?\bunsubscribe\b/i;

  const match = text.match(replyOptOutRegex);
  const matched = !!match || replyStopToUnsubscribeRegex.test(text);
  if (!matched) return [0, []];

  const keywordRaw = (match?.[1] || 'unsubscribe').toLowerCase().trim();
  const keywordNormalized = keywordRaw.replace(/\s+/g, ' ');

  return [
    2,
    [
      `Manual opt-out request: asks recipient to reply "${keywordNormalized}" (no List-Unsubscribe header)`,
    ],
  ];
}

export function checkBdrPhrasesWithDetails(text: string): BdrPhraseDetails {
  /**
   * Check for classic BDR phrases in text with detailed results.
   *
   * @param text - Text to check.
   * @returns Detailed BDR phrase information.
   */
  // High-confidence spam phrases (score +1 each)
  const highConfidencePhrases = [
    '15 minutes',
    'circle back',
    'just checking in',
    'wanted to reach out',
    "let's schedule a call",
    'cold outreach',
    'automated email',
    // Asking for reply instead of unsubscribe link (strong spam indicator)
    'if this is not relevant please let me know',
    'if this is not relevant let me know',
    'if not relevant please let me know',
    'if not relevant let me know',
    'reply with "no thanks"',
    "reply with 'no thanks'",
    'reply with no thanks',
    'reply "no thanks"',
    "reply 'no thanks'",
    'reply no thanks',
    "let me know if you're not interested",
    'let me know if you are not interested',
    'let me know if not interested',
    // B2B staffing/outsourcing sales patterns
    'nearshore',
    'staff augmentation',
    'augment your team',
    'open to a short discussion',
    'open to a brief discussion',
    'open to a quick discussion',
    'open to exploring',
    'would you consider',
    // B2B lead gen/outbound sales patterns
    'booked calls',
    'booked meetings',
    'pipeline on demand',
    'curious how we do it',
  ];

  // Lower-confidence phrases (score +0.5 each, but require multiple)
  const lowConfidencePhrases = [
    'quick question',
    'touch base',
    'follow up',
    'hope this finds you well',
    'would love to connect',
    // B2B staffing/outsourcing patterns (need 2+ to score)
    'scale your',
    'expand capacity',
    'dedicated team',
    'extend your team',
    'support your goals',
    'time zone overlap',
    'latam',
    // B2B lead gen/outbound sales patterns (need 2+ to score)
    'handle your outbound',
    'handle all their outbound',
    'hit your quotas',
  ];

  const textLower = text.toLowerCase();
  const highConfidenceFound: string[] = [];
  const lowConfidenceFound: string[] = [];

  for (const phrase of highConfidencePhrases) {
    if (textLower.includes(phrase)) {
      highConfidenceFound.push(phrase);
    }
  }

  for (const phrase of lowConfidencePhrases) {
    if (textLower.includes(phrase)) {
      lowConfidenceFound.push(phrase);
    }
  }

  // Extract subject and snippet lengths (if text contains both)
  // For now, we'll use the full text length
  const subjectLength = 0; // Will be set by caller if available
  const snippetLength = text.length;
  const totalLength = text.length;

  return {
    high_confidence: {
      phrases_found: highConfidenceFound,
      count: highConfidenceFound.length,
    },
    low_confidence: {
      phrases_found: lowConfidenceFound,
      count: lowConfidenceFound.length,
      scored: lowConfidenceFound.length >= 2,
    },
    text_checked: {
      subject_length: subjectLength,
      snippet_length: snippetLength,
      total_length: totalLength,
    },
  };
}
